import { AnimationCallbackEvent, Component, computed, signal } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { DeviceService } from 'src/services/device.service';
import { MobileExplainTooltipSheetService } from 'src/services/mobile-explain-tooltip-sheet.service';
import { NativeSafeZoneService } from 'src/services/native-safe-zone.service';
import { MOBILE_BOTTOM_SHEET_TOP_OFFSET } from 'src/util/constants';

@Component({
  selector: 'liberro-mobile-explain-tooltip-sheet',
  standalone: false,
  templateUrl: './liberro-mobile-explain-tooltip-sheet.component.html',
  styleUrl: './liberro-mobile-explain-tooltip-sheet.component.less',
})
export class LiberroMobileExplainTooltipSheetComponent {
  private readonly closeDistancePx = 28;
  private readonly closeVelocityPxPerMs = 0.4;
  private readonly closeAnimationMs = 280;
  private readonly backdropFadeDistancePx = 240;

  private dragPointerId: number | null = null;
  private dragStartY = 0;
  private lastMoveY = 0;
  private lastMoveTime = 0;
  private dragVelocity = 0;

  public readonly dragOffsetPx = signal(0);
  public readonly isDragging = signal(false);

  public constructor(
    public tooltipSheetService: MobileExplainTooltipSheetService,
    private nativeSafeZoneService: NativeSafeZoneService,
    private deviceService: DeviceService,
  ) {}

  public maxHeight = computed(() =>
    `calc(100svh - ${MOBILE_BOTTOM_SHEET_TOP_OFFSET}px - ${this.nativeSafeZoneService.paddings.top + this.nativeSafeZoneService.paddings.navbarHeight}px)`
  );

  public dragProgress = computed(() => Math.min(1, this.dragOffsetPx() / this.backdropFadeDistancePx));

  public onDragStart(event: PointerEvent): void {
    const target = event.target as HTMLElement;
    if (
      this.dragPointerId !== null
      || !(target.closest('.tooltip-header') || target.classList.contains('overlay-mobile-sheet'))
      || target.closest('.tooltip-close-icon')
    ) {
      return;
    }
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.dragPointerId = event.pointerId;
    this.dragStartY = this.lastMoveY = event.clientY;
    this.lastMoveTime = event.timeStamp;
    this.dragVelocity = 0;
    this.isDragging.set(true);
  }

  public onDragMove(event: PointerEvent): void {
    if (event.pointerId !== this.dragPointerId) {
      return;
    }
    const elapsedMs = event.timeStamp - this.lastMoveTime;
    if (elapsedMs > 0) {
      this.dragVelocity = (event.clientY - this.lastMoveY) / elapsedMs;
    }
    this.lastMoveY = event.clientY;
    this.lastMoveTime = event.timeStamp;
    this.dragOffsetPx.set(Math.max(0, event.clientY - this.dragStartY));
  }

  public onDragEnd(event: PointerEvent): void {
    if (event.pointerId !== this.dragPointerId) {
      return;
    }
    this.dragPointerId = null;
    this.isDragging.set(false);
    if (this.dragOffsetPx() >= this.closeDistancePx || this.dragVelocity >= this.closeVelocityPxPerMs) {
      this.tooltipSheetService.close();
    } else {
      this.dragOffsetPx.set(0);
    }
  }

  public onSheetLeave(event: AnimationCallbackEvent): void {
    // The leaving view is no longer change-detected, so its state classes must be toggled directly.
    event.target.classList.remove('is-dragging');
    event.target.classList.add('is-closing');
    setTimeout(() => {
      this.dragOffsetPx.set(0);
      event.animationComplete();
    }, this.closeAnimationMs);
  }

  public onLearnMoreClick(url: string): void {
    if (this.deviceService.isNativePlatform) {
      Browser.open({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
}
